/**
 * useCallRecording (Welle 7e / pre97) — client-seitige Aufzeichnung beim Berater.
 *
 * E2EE ist immer an → der Berater-Client ist der EINZIGE Ort mit entschlüsselten
 * Medien. Aufzeichnung daher zwingend hier. MiFID-Kern ist die Tonspur, deshalb
 * nimmt der Hook AUDIO getrennt pro Partei auf (berater/besucher) plus eine
 * kombinierte Spur, baut daraus ein kanonisches/markt-kompatibles Paket
 * (Manifest + Protokoll + SHA-256, ZIP) und liefert es über einen Delivery-Adapter
 * aus (FSA-Ordner = FI-Hot-Folder).
 *
 * pre97: rein LOKAL. Der frühere optionale Backend-Pfad (/api/recording/*) ist
 * entfernt — er löste über den globalen apiFetch-401-Handler einen Auto-Logout aus
 * und riss damit den laufenden Call ab. Der serverseitige Audit-/Upload-Pfad kehrt
 * sauber zurück, sobald das RecordingModule serverseitig eingebunden und autorisiert ist.
 *
 * Streng gated: Aufzeichnung startet nur nach AKTIVER Zustimmung auf der Stele
 * (Touch, keine mündliche) — der Consent-Handshake liegt in InCall/useWebRtc.
 */

import { useCallback, useRef, useState } from 'react';
import { buildPackage, type RecordedTrack, type RecordingMeta, type PartyName } from '@/lib/recordingPackage';
import { deliverRecording, logRecordingDelivery, getRecipientKey, type DeliveryResult } from '@/lib/recordingDelivery';
import { encryptToRecipient } from '@/lib/recordingCrypto';
import { DEFAULT_EXPORT_PROFILE, DEFAULT_DELIVERY, type DeliveryAdapterId } from '@/lib/recordingProfiles';
import { RecordingSender, type RecMeta } from '@/lib/recordingTransfer';

/**
 * Welle 12 (pre134): Versuche, das fertige Paket per direktem P2P-DataChannel an einen
 * angedockten Companion (Terminal-Server) zu übergeben. Liefert true bei Empfangsquittung,
 * false (nach kurzem Timeout) wenn kein Companion da ist → Aufrufer macht normale Auslieferung.
 */
async function tryCompanionTransfer(blob: Blob, meta: RecMeta, answerTimeoutMs = 4500): Promise<boolean> {
  const sender = new RecordingSender((sig) => {
    window.dispatchEvent(new CustomEvent('bamoa:remote-state', { detail: { __rec: sig } }));
  });
  const onCmd = (e: Event) => {
    const d = (e as CustomEvent).detail;
    if (d?.action === '__rec' && d.rec) void sender.onSignal(d.rec);
  };
  window.addEventListener('bamoa:remote-cmd', onCmd);
  try {
    return await sender.start(blob, meta, { answerTimeoutMs });
  } catch {
    return false;
  } finally {
    window.removeEventListener('bamoa:remote-cmd', onCmd);
  }
}

export interface RecordingStartOpts {
  /** Ablage-Adapter laut Policy (Default: lokaler Ordner). */
  delivery?: DeliveryAdapterId;
  /** Empfänger-Public-Key laut Policy (überschreibt den lokal gesetzten). */
  recipientKey?: string | null;
}

// Basis-Guard: Aufzeichnung braucht ein Thema. Die eigentliche Zulässigkeit (welche Themen,
// an/aus) entscheidet die mandantenweite Policy upstream in InCall (policyAllowsTopic).
export function topicRequiresRecording(topic?: string): boolean {
  return !!topic && topic.trim().length > 0;
}
/** MiFID II greift nur bei Wertpapier-/Anlagebezug; Kredit/Baufi fallen unter andere Regime. */
export function topicIsMifid(topic?: string): boolean {
  return topic?.toLowerCase() === 'geldanlage';
}

const TIMESLICE_MS = 4000;

export interface RecordingParties {
  berater: MediaStream | null;
  besucher: MediaStream | null;
}

export interface RecordingHandle {
  recording: boolean;
  lastDelivery: DeliveryResult | null;
  start: (callId: string, topic: string, parties: RecordingParties, meta?: Partial<RecordingMeta>, opts?: RecordingStartOpts) => Promise<void>;
  stop: () => Promise<void>;
}

function pickAudioMime(): string {
  if (typeof MediaRecorder === 'undefined') return 'audio/webm';
  return MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
}

interface PartyRecorder {
  party: PartyName;
  rec: MediaRecorder;
  chunks: BlobPart[];
}

export function useCallRecording(
  onStateChange?: (active: boolean, topic?: string) => void,
): RecordingHandle {
  const [recording, setRecording] = useState(false);
  const [lastDelivery, setLastDelivery] = useState<DeliveryResult | null>(null);

  const recordersRef = useRef<PartyRecorder[]>([]);
  const startedAtRef = useRef<number>(0);
  const metaRef = useRef<RecordingMeta | null>(null);
  const optsRef = useRef<RecordingStartOpts>({});

  const start = useCallback(async (
    callId: string, topic: string, parties: RecordingParties, meta?: Partial<RecordingMeta>, opts?: RecordingStartOpts,
  ) => {
    if (!topicRequiresRecording(topic)) {
      console.warn('[rec] Topic erfordert keine Aufzeichnung — Start abgelehnt');
      return;
    }
    if (recordersRef.current.length) return;
    optsRef.current = opts ?? {};

    const mime = pickAudioMime();
    const now = Date.now();
    startedAtRef.current = now;

    const beraterAudio = parties.berater?.getAudioTracks() ?? [];
    const besucherAudio = parties.besucher?.getAudioTracks() ?? [];

    const recs: PartyRecorder[] = [];
    const makeRecorder = (party: PartyName, tracks: MediaStreamTrack[]) => {
      if (!tracks.length) return;
      const ms = new MediaStream(tracks);
      const rec = new MediaRecorder(ms, { mimeType: mime, audioBitsPerSecond: 64_000 });
      const entry: PartyRecorder = { party, rec, chunks: [] };
      rec.ondataavailable = (e) => { if (e.data && e.data.size) entry.chunks.push(e.data); };
      recs.push(entry);
    };
    makeRecorder('berater', beraterAudio);
    makeRecorder('besucher', besucherAudio);
    makeRecorder('kombiniert', [...beraterAudio, ...besucherAudio]);

    if (!recs.length) {
      console.warn('[rec] keine Audio-Spuren — Aufzeichnung nicht möglich');
      return;
    }

    metaRef.current = {
      callId, topic,
      mifidRelevant: topicIsMifid(topic),
      startedAt: now, endedAt: now,
      agents: meta?.agents ?? [],
      guests: meta?.guests ?? [],
      consentMode: meta?.consentMode ?? 'einwilligung',
      consentAt: meta?.consentAt ?? now,
      kundenname: meta?.kundenname, personenNummer: meta?.personenNummer,
      ordernummer: meta?.ordernummer, geburtsdatum: meta?.geburtsdatum,
      telefonnummer: meta?.telefonnummer, filiale: meta?.filiale,
      retentionYears: meta?.retentionYears ?? 5,
      device: meta?.device,
    };

    for (const r of recs) r.rec.start(TIMESLICE_MS);
    recordersRef.current = recs;
    setRecording(true);
    onStateChange?.(true, topic);
    console.log('[rec] Aufzeichnung gestartet', { callId, topic, spuren: recs.map((r) => r.party) });
  }, [onStateChange]);

  const stop = useCallback(async () => {
    const recs = recordersRef.current;
    if (!recs.length) return;

    await Promise.all(recs.map((r) => new Promise<void>((resolve) => {
      r.rec.onstop = () => resolve();
      try { r.rec.stop(); } catch { resolve(); }
    })));
    recordersRef.current = [];
    setRecording(false);
    onStateChange?.(false);

    const meta = metaRef.current;
    metaRef.current = null;
    const opts = optsRef.current;
    optsRef.current = {};
    const adapter: DeliveryAdapterId = opts.delivery ?? DEFAULT_DELIVERY;
    if (meta) meta.endedAt = Date.now();

    try {
      const tracks: RecordedTrack[] = recs
        .filter((r) => r.chunks.length)
        .map((r) => ({ party: r.party, blob: new Blob(r.chunks, { type: 'audio/webm' }), ext: 'webm' }));
      if (!tracks.length || !meta) { console.warn('[rec] keine Daten zum Verpacken'); return; }

      const pkg = await buildPackage(DEFAULT_EXPORT_PROFILE, tracks, meta);

      // Schlüsselhoheit: Ist ein Empfänger-Public-Key gesetzt, wird das Paket NUR
      // verschlüsselt abgelegt. Schlägt die Verschlüsselung fehl, wird NICHTS abgelegt
      // (kein Klartext-Leak).
      let outPkg = pkg;
      const recipient = opts.recipientKey ?? await getRecipientKey();
      if (recipient) {
        try {
          const raw = new Uint8Array(await pkg.blob.arrayBuffer());
          const enc = await encryptToRecipient(recipient, raw);
          outPkg = {
            ...pkg,
            blob: new Blob([enc.slice().buffer], { type: 'application/pgp-encrypted' }),
            fileName: pkg.fileName + '.gpg',
          };
        } catch (e) {
          console.error('[rec] GPG-Verschlüsselung fehlgeschlagen — Ablage abgebrochen (kein Klartext)', e);
          setLastDelivery({ ok: false, method: adapter, reason: 'encrypt-error' });
          await logRecordingDelivery({
            callId: meta.callId, fileName: pkg.fileName + '.gpg', ok: false,
            method: adapter, profile: pkg.profile, rootHash: pkg.rootHash, at: Date.now(),
          });
          return;
        }
      }

      // Welle 12 (pre134): Ist ein Companion (Terminal-Server) angedockt, das Paket per
      // direktem P2P-DataChannel dorthin übergeben — es landet dann im Ordner auf dem TS und
      // verlässt nie den Server. Kein Companion da → kurzer Timeout, dann normale Auslieferung.
      const recMeta: RecMeta = {
        fileName: outPkg.fileName, size: outPkg.blob.size,
        mime: outPkg.blob.type || 'application/octet-stream',
        rootHash: pkg.rootHash, callId: meta.callId,
      };
      const viaCompanion = await tryCompanionTransfer(outPkg.blob, recMeta);
      let result: DeliveryResult;
      if (viaCompanion) {
        result = { ok: true, method: 'companion', name: outPkg.fileName };
      } else {
        result = await deliverRecording(adapter, outPkg, meta.callId);
      }
      setLastDelivery(result);
      // REC-Hinweis für die Nachbearbeitung persistieren (je Call).
      await logRecordingDelivery({
        callId: meta.callId, fileName: outPkg.fileName, ok: result.ok,
        method: result.method, profile: pkg.profile, rootHash: pkg.rootHash, at: Date.now(),
      });
      console.log('[rec] Paket ausgeliefert', { datei: outPkg.fileName, verschluesselt: Boolean(recipient), profil: pkg.profile, ergebnis: result, rootHash: pkg.rootHash });
    } catch (err) {
      console.error('[rec] Paketierung/Auslieferung fehlgeschlagen', err);
      setLastDelivery({ ok: false, method: DEFAULT_DELIVERY, reason: 'error' });
    }
  }, [onStateChange]);

  return { recording, lastDelivery, start, stop };
}
